import React, { Fragment } from "react";
import { useSelector } from "react-redux";
import { Form } from "react-bulma-components";
import { enumeratedDataValuesSlice } from "redux/features/enumeratedDataValues";
import TrimmedInput from "common-frontend/util/trimmedInput";
import { sortOn, caseInsensitive } from "util/sort";

const { Control, Checkbox } = Form;

function PopulationGroupComponent({
  orig,
  getValue,
  setValue,
  forSummary,
  RenderField,
}) {
  const enumeratedDataValues = useSelector(enumeratedDataValuesSlice.selector);
  const originalGroups = orig.population_groups;
  // A "group" in the populationGroups list is one of the
  // checkbox-selectable population groups.
  const populationGroups = enumeratedDataValues.data.population_groups;

  const populationGroupLookup = Object.fromEntries(
    populationGroups.map((g) => [g.symbolic_id, g])
  );

  // A "group specifier" is a string which is the symbolic id of one
  // of the groups, optionally followed by a hyphen and a write-in
  // specification if the group is something like "other" that permits
  // write-ins.
  const currentGroupSpecifierList = getValue("population_groups");

  // A "group selection" is an object with entries "group" and
  // "writeIn" containing the selected group and any writeIn data, and
  // sortKey for sorting. (The writeIn entry is null if the group
  // doesn't support write-in information.)
  const parseSpecifier = (specifier) => {
    const groupId = specifier.replace(/-.*/, "");
    const group = populationGroupLookup[groupId];
    if (!group) return null;
    const writeIn = !group.supports_write_in
      ? null
      : groupId === specifier
      ? ""
      : specifier.replace(/^.*?-/, "");
    return { group, writeIn, sortKey: group.sort_key };
  };

  const groupSelectionList = currentGroupSpecifierList
    ? currentGroupSpecifierList
        .map(parseSpecifier)
        .filter((item) => !!item)
        .sort(sortOn("sortKey", caseInsensitive))
    : null;

  const groupSelectionLookup = groupSelectionList
    ? Object.fromEntries(
        groupSelectionList.map((selection) => [
          selection.group.symbolic_id,
          selection,
        ])
      )
    : {};

  const originalSelectionLookup = originalGroups
    ? Object.fromEntries(
        originalGroups
          .map(parseSpecifier)
          .filter((selection) => !!selection)
          .map((selection) => [selection.group.symbolic_id, selection])
      )
    : {};

  const adjustSpecifier = (groupId, newSpecifier) => {
    const otherSpecifiers = currentGroupSpecifierList
      ? currentGroupSpecifierList.filter((specifier) => {
          const selection = parseSpecifier(specifier);
          return selection && selection.group.symbolic_id !== groupId;
        })
      : [];
    const newSpecifierList = newSpecifier
      ? [...otherSpecifiers, newSpecifier]
      : otherSpecifiers;
    setValue(
      "population_groups",
      newSpecifierList.length > 0 ? newSpecifierList.sort() : null
    );
  };

  const selectGroup = (group) => {
    if (!groupSelectionLookup[group.symbolic_id]) {
      const newSpecifier =
        group.symbolic_id +
        (group.supports_write_in
          ? "-" +
            (originalSelectionLookup[group.symbolic_id]
              ? originalSelectionLookup[group.symbolic_id]
              : "")
          : "");
      adjustSpecifier(group.symbolic_id, newSpecifier);
    }
  };

  const selectOrDeselectGroup = (group, select) => {
    if (select) selectGroup(group);
    else adjustSpecifier(group.symbolic_id, null);
  };

  const setWriteIn = (group, writeIn) => {
    const newSpecifier = group.symbolic_id + "-" + writeIn;
    adjustSpecifier(group.symbolic_id, newSpecifier);
  };

  const setOrClearPreferNotToSay = (bool) => {
    if (bool) setValue("population_groups", []);
    else if (
      currentGroupSpecifierList &&
      currentGroupSpecifierList.length === 0
    )
      setValue("population_groups", null);
  };

  const selectionAsText = !groupSelectionList ? null : groupSelectionList.length ===
    0 ? (
    "Prefer not to say"
  ) : (
    <Fragment>
      {groupSelectionList.map((selection) => (
        <p key={selection.group.symbolic_id}>
          {selection.group.label}
          {selection.writeIn == null ? null : selection.writeIn === "" ? (
            <i> (unspecified)</i>
          ) : (
            " (" + selection.writeIn + ")"
          )}
        </p>
      ))}{" "}
    </Fragment>
  );

  return (
    <Fragment>
      <RenderField
        label="Population Group"
        question={
          <Fragment>
            Please specify which of the following group(s) you identify with. If
            you have a mixed-ethnicity background please feel free to specify
            more than one.
          </Fragment>
        }
        value={selectionAsText}
      >
        <Fragment>
          {populationGroups.map((group) => {
            const selection = groupSelectionLookup[group.symbolic_id];
            return (
              <Fragment key={group.symbolic_id}>
                <Control>
                  <Checkbox
                    checked={!!selection}
                    onChange={(e) =>
                      selectOrDeselectGroup(group, e.target.checked)
                    }
                  >
                    {group.label}
                  </Checkbox>
                </Control>
                {selection && selection.group.supports_write_in ? (
                  <TrimmedInput
                    type="text"
                    value={selection.writeIn}
                    setValue={(v) => setWriteIn(group, v)}
                  />
                ) : null}
              </Fragment>
            );
          })}
          <Control>
            <Checkbox
              checked={
                currentGroupSpecifierList &&
                currentGroupSpecifierList.length === 0
              }
              onChange={(e) => setOrClearPreferNotToSay(e.target.checked)}
            >
              Prefer Not to Say
            </Checkbox>
          </Control>
        </Fragment>
      </RenderField>
    </Fragment>
  );
}

export default PopulationGroupComponent;
